import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Legend,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import i18next from 'i18next';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { Tooltip } from '@commons/utils/styledLibraryComponents';
import ComponentLoader from '@commons/DeepsightComponents/ComponentLoader';

import CollapsibleTable from '../CollapsibleTable';

import {
  ChartContainer,
  MessageContainer,
  MessageContent,
  FeedbackContainer,
  FeedbackButton,
  FeedbackIcon,
  ScoreContainer,
  DownloadContainer,
  DownloadButton,
  DownloadIcon,
  RetryContainer,
} from './styledComponents';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const PROCESSING_MESSAGE_STATUS = 'processing';
const ERROR_MESSAGE_STATUS = 'error';

// Allows to open links in a new tab
const LinkRenderer = (props) => (
  <a href={props.href} rel="noopener noreferrer" target="_blank">
    {props.children}
  </a>
);

const MessageItem = ({
  message,
  handleFeedBack,
  handleExportTable,
  handleRetryMessage,
  isRetryInProgress,
}) => {
  const { id, content, isUser, chart, score, table, status, has_retried } = message;

  const renderChart = () => {
    if (!chart) return null;

    const chartProps = {
      data: chart.data,
      options: {
        responsive: true,
        maintainAspectRatio: true,
      },
    };

    switch (chart.type) {
      case 'line':
        return <Line {...chartProps} />;
      case 'bar':
        return <Bar {...chartProps} />;
      case 'pie':
        return <Pie {...chartProps} />;
      case 'doughnut':
        return <Doughnut {...chartProps} />;
      default:
        return null;
    }
  };

  const renderRetryButton = () => {
    if (!isUser) return null;

    if (status === ERROR_MESSAGE_STATUS) {
      const isDisabled = has_retried || isRetryInProgress;
      const retryImage = (
        <img
          alt="Retry"
          src="/images/inpulse/agent-refresh-black.svg"
          style={{
            width: '16px',
            height: '16px',
            marginRight: '8px',
            opacity: isDisabled ? 0.3 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            filter: isDisabled ? 'grayscale(100%)' : 'none',
          }}
          onClick={() => !isDisabled && handleRetryMessage(id)}
        />
      );

      if (isDisabled) {
        return (
          <Tooltip
            renderTooltipTrigger={() => retryImage}
            text={
              has_retried
                ? i18next.t('AGENT.AGENT.RETRY_BUTTON_DISABLED_TOOLTIP')
                : i18next.t('AGENT.AGENT.RETRY_BUTTON_DISABLED_BECAUSE_RETRY_IN_PROGRESS_TOOLTIP')
            }
          />
        );
      }
      return retryImage;
    }

    if (status === PROCESSING_MESSAGE_STATUS && has_retried) {
      return (
        <RetryContainer>
          <ComponentLoader color="DEFAULT" height={16} width={16} />
        </RetryContainer>
      );
    }

    return null;
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        alignItems: 'center',
      }}
    >
      {renderRetryButton()}
      <MessageContainer isUser={isUser}>
        {table && (
          <DownloadContainer>
            <DownloadButton onClick={() => handleExportTable(table)}>
              <DownloadIcon src="/images/inpulse/file-download-black-small.svg" />
            </DownloadButton>
          </DownloadContainer>
        )}
        <MessageContent isUser={isUser}>
          <ReactMarkdown
            components={{
              table: CollapsibleTable,
              a: LinkRenderer,
            }}
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
            remarkRehypeOptions={{ passThrough: ['link'] }}
          >
            {content}
          </ReactMarkdown>
        </MessageContent>
        {chart && <ChartContainer>{renderChart()}</ChartContainer>}
        {!isUser && !score && (
          <FeedbackContainer>
            <FeedbackButton onClick={() => handleFeedBack(id, 1)}>
              <FeedbackIcon src="/images/inpulse/thumb-up-black.svg" />
            </FeedbackButton>
            <FeedbackButton onClick={() => handleFeedBack(id, -1)}>
              <FeedbackIcon src="/images/inpulse/thumb-down-black.svg" />
            </FeedbackButton>
          </FeedbackContainer>
        )}
        {!isUser && score && (
          <ScoreContainer>
            <FeedbackIcon
              src={
                score === 1
                  ? '/images/inpulse/thumb-up-black.svg'
                  : '/images/inpulse/thumb-down-black.svg'
              }
            />
          </ScoreContainer>
        )}
      </MessageContainer>
    </div>
  );
};

export default MessageItem;
